import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronDown,
  FileText,
  Receipt,
  Users,
  Banknote,
  Clock3,
  Briefcase,
  CreditCard,
  PenTool,
  Sparkles,
  Building2,
  Hammer,
  Stethoscope,
  Wrench,
  Store,
  Scissors,
  Landmark,
  BookOpenCheck,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { listReviews, type Review } from '../api';
import { useLivePrices } from '../useLivePrices';
import {
  FALLBACK_PRICES, FALLBACK_SEAT_BASES, FALLBACK_INCLUDED_SEATS,
  SEAT_LABEL, computeMonthlyTotal,
} from '../pricingMath';
import { Avatar, Stars } from './CustomersPage';
import { MarketingPageHead } from '../MarketingPageHead';
import { BrowserFrame } from '../components/BrowserFrame';
import { Parallax } from '../components/motion';
import { JsonLd, siteOrigin } from '../seo/JsonLd';
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildFaqPageSchema,
} from '../seo/schema';
import dashboardScreen from '../assets/screens/dashboard.png';
import attendanceScreen from '../assets/screens/attendance.png';
import invoicesScreen from '../assets/screens/invoices.png';
import projectsScreen from '../assets/screens/projects.png';

/**
 * LandingPage
 * --------------------------------------------------------------
 * Worqly marketing homepage. Production-grade SaaS structure
 * following the canonical anatomy:
 *
 *   1. Hero — headline + sub + dual CTA + product visual
 *   2. Social proof — "Built by operators in" logo strip
 *   3. Feature highlights — 6 module cards
 *   4. How it works — 3 steps
 *   5. Testimonials — placeholder cards (no fabricated names)
 *   6. Pricing teaser — module table preview
 *   7. FAQ — 6 honest questions
 *   8. Footer CTA — email capture redirecting to /signup
 *
 * Brand voice: confident, plain, operator-friendly. No demo
 * copy, no Lorem ipsum, no fake customer names.
 *
 * Light + dark mode: every section uses explicit `dark:` variants.
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export function LandingPage() {
  const origin = siteOrigin();
  const orgSchema = React.useMemo(() => buildOrganizationSchema({ origin }), [origin]);
  const siteSchema = React.useMemo(() => buildWebSiteSchema({ origin }), [origin]);

  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Run your people, finances & operations in one place"
        description="Time & attendance, payroll, invoicing, e-sign, projects, and monitoring — connected modules with one clear bill. 7 days free, no card."
        titleTemplate={false}
      />
      <JsonLd id="organization" schema={orgSchema} />
      <JsonLd id="website" schema={siteSchema} />
      <Hero />
      <SocialProof />
      <Pillars />
      <ConnectedWorkflow />
      <Testimonials />
      <PricingTeaser />
      <FAQ />
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 1. HERO
 * ───────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-12 pb-12 lg:pt-20 lg:pb-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={STAGGER}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
        >
          <div className="lg:col-span-7 text-center lg:text-left">
            <motion.div variants={FADE_UP}>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                  bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                  dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none
                  hover:bg-white transition-colors"
              >
                <Sparkles className="h-3 w-3 text-coral" />
                7 days free. No card required.
                <ArrowRight className="h-3 w-3 text-ink/45 dark:text-white/60" />
              </Link>
            </motion.div>

            <motion.h1
              variants={FADE_UP}
              className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
            >
              Run your people, finances{' '}
              <span className="italic font-serif text-coral">&amp; operations</span> in one
              place.
            </motion.h1>

            <motion.p
              variants={FADE_UP}
              className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto lg:mx-0 text-pretty"
            >
              Time &amp; attendance, payroll, invoicing, e-sign, projects, and monitoring —
              connected modules that share one workspace instead of four disconnected logins.
              One clear bill for whichever ones you turn on.
            </motion.p>

            <motion.div
              variants={FADE_UP}
              className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start"
            >
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
              >
                Start free trial
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact?topic=demo"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-ink text-[14.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-50 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
              >
                Book a demo
              </Link>
            </motion.div>

            <motion.p
              variants={FADE_UP}
              className="mt-5 text-[12.5px] text-ink/55 dark:text-white/55 flex flex-wrap items-center gap-x-2 gap-y-1 justify-center lg:justify-start"
            >
              <span>No card to start</span>
              <span className="text-ink/25 dark:text-white/25">·</span>
              <span>Add card before day 8</span>
              <span className="text-ink/25 dark:text-white/25">·</span>
              <span>Cancel any time</span>
            </motion.p>
          </div>

          <motion.div variants={FADE_UP} className="lg:col-span-5">
            <HeroVisual />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/**
 * HeroVisual — a real screenshot of the Worqly dashboard (demo workspace)
 * inside a CSS browser frame. Replaces the earlier hand-built dashboard
 * mock now that a genuine screenshot exists.
 */
function HeroVisual() {
  return (
    <div className="relative">
      {/* Ambient drift keeps the hero alive after the entry animation settles —
       *  18s cycle, ~2% travel, so it registers peripherally without competing
       *  with the copy. Flattened under prefers-reduced-motion. */}
      <div aria-hidden className="absolute -inset-8 bg-coral/20 blur-3xl rounded-full -z-10 animate-mesh-drift motion-ambient" />
      <BrowserFrame url="worqly.io/dashboard" className="relative">
        <img
          src={dashboardScreen}
          alt="Worqly dashboard for a demo workspace, showing revenue collected, open invoices, team headcount, and active projects."
          className="w-full h-auto block"
          width={1440}
          height={900}
        />
      </BrowserFrame>

      {/* Floating "PAID" tag */}
      <div className="absolute -top-3 -right-3 z-10">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lime-neon text-ink rounded-full text-[10.5px] font-bold uppercase tracking-[0.10em] shadow-card">
          Paid · $2,840
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 2. SOCIAL PROOF
 * ───────────────────────────────────────────────────────────── */

function SocialProof() {
  const operators: Array<{ label: string; Icon: React.ComponentType<{ className?: string }> }> = [
    { label: 'Studios',       Icon: Hammer },
    { label: 'Clinics',       Icon: Stethoscope },
    { label: 'Trades',        Icon: Wrench },
    { label: 'Shops',         Icon: Store },
    { label: 'Salons',        Icon: Scissors },
    { label: 'Agencies',      Icon: Building2 },
  ];

  return (
    <section className="py-12 lg:py-14 border-y border-ink/8 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] backdrop-blur-md">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-ink/55 dark:text-white/55 mb-7">
          Built by operators in
        </p>
        <ul className="grid grid-cols-3 md:grid-cols-6 gap-4 lg:gap-6">
          {operators.map(({ label, Icon }) => (
            <li
              key={label}
              className="flex flex-col items-center gap-2 py-2 rounded-2xl text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white transition-colors"
            >
              <Icon className="h-6 w-6" />
              <span className="text-[12.5px] font-semibold">{label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 3. PILLARS — People / Operations / Finance
 * ───────────────────────────────────────────────────────────── */

type PillarChip = { label: string; Icon: React.ComponentType<{ className?: string }> };

type Pillar = {
  eyebrow: string;
  title: string;
  titleAccent: string;
  body: string;
  screen: string;
  alt: string;
  url: string;
  chips: PillarChip[];
  reverse: boolean;
};

const PILLARS: Pillar[] = [
  {
    eyebrow: 'People',
    title: 'Attendance and leave,',
    titleAccent: 'without the spreadsheet.',
    body:
      'Clock-in/clock-out, leave requests and approvals, and penalty rules live in one place — and feed straight into payroll runs, so hours worked and days off are never re-typed.',
    screen: attendanceScreen,
    alt: 'Worqly attendance calendar for a demo workspace, showing daily clock-in/out hours and leave days across a month.',
    url: 'worqly.io/dashboard/hr/attendance',
    chips: [
      { label: 'Time & attendance', Icon: Clock3 },
      { label: 'Leave & approvals', Icon: Users },
      { label: 'Payroll runs', Icon: Banknote },
    ],
    reverse: false,
  },
  {
    eyebrow: 'Operations',
    title: 'Projects and signatures,',
    titleAccent: 'tracked to the task.',
    body:
      'Track tasks, hours, and margin per project, then send a contract for e-signature without leaving the workspace — no separate DocuSign subscription, no copy-pasting project names between tools.',
    screen: projectsScreen,
    alt: 'Worqly projects board for a demo workspace, showing tasks, tracked hours, and project budgets.',
    url: 'worqly.io/dashboard/projects',
    chips: [
      { label: 'Projects & time', Icon: Briefcase },
      { label: 'E-Sign', Icon: PenTool },
      { label: 'Reports', Icon: BookOpenCheck },
    ],
    reverse: true,
  },
  {
    eyebrow: 'Finance',
    title: 'Invoicing and payments,',
    titleAccent: 'reconciled automatically.',
    body:
      'Send branded invoices with a Stripe pay link, track what\'s paid and what\'s overdue, and let expenses and payments sync to QuickBooks — one ledger, not three exports a month.',
    screen: invoicesScreen,
    alt: 'Worqly invoices list for a demo workspace, showing invoice status, amounts, and due dates.',
    url: 'worqly.io/dashboard/invoicing',
    chips: [
      { label: 'Invoicing', Icon: FileText },
      { label: 'Payments', Icon: CreditCard },
      { label: 'Expenses', Icon: Receipt },
    ],
    reverse: false,
  },
];

function Pillars() {
  return (
    <section id="pillars" className="py-20 lg:py-24">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-14 lg:mb-16"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">What's inside</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            Real screens. Real workflows. One workspace.
          </motion.h2>
          <motion.p variants={FADE_UP} className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl">
            Screenshots below are of Worqly's demo workspace ("Brightline Studio") — turn on only
            the modules your business needs, and we bill for those.
          </motion.p>
        </motion.div>

        <div className="space-y-16 lg:space-y-20">
          {PILLARS.map((p) => (
            <PillarRow key={p.eyebrow} pillar={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PillarRow({ pillar }: { pillar: Pillar }) {
  const { eyebrow, title, titleAccent, body, screen, alt, url, chips, reverse } = pillar;
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
      variants={STAGGER}
      className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center"
    >
      <div className={'lg:col-span-5' + (reverse ? ' lg:order-2' : ' lg:order-1')}>
        <motion.div variants={FADE_UP}>
          <Eyebrow className="!text-coral">{eyebrow}</Eyebrow>
        </motion.div>
        <motion.h3
          variants={FADE_UP}
          className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.02em] leading-[1.1] text-balance"
        >
          {title} <span className="italic font-serif text-coral">{titleAccent}</span>
        </motion.h3>
        <motion.p variants={FADE_UP} className="mt-4 text-[15px] text-ink/65 dark:text-white/65 leading-relaxed">
          {body}
        </motion.p>
        <motion.ul variants={FADE_UP} className="mt-6 flex flex-wrap gap-2">
          {chips.map(({ label, Icon }) => (
            <li
              key={label}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/85 dark:bg-white/[0.06] ring-1 ring-ink/8 dark:ring-white/10 shadow-pill text-[12.5px] font-semibold text-ink dark:text-white"
            >
              <Icon className="h-3.5 w-3.5 text-coral" />
              {label}
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.div
        variants={FADE_UP}
        className={'lg:col-span-7' + (reverse ? ' lg:order-1' : ' lg:order-2')}
      >
        <Parallax distance={48}>
          <BrowserFrame url={url} className="hover-lift hover:shadow-[0_32px_80px_-28px_rgba(26,26,26,0.4)]">
            <img src={screen} alt={alt} className="w-full h-auto block" width={1440} height={900} />
          </BrowserFrame>
        </Parallax>
      </motion.div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 4. CONNECTED WORKFLOW STRIP
 * ───────────────────────────────────────────────────────────── */

type WorkflowStep = { label: string; Icon: React.ComponentType<{ className?: string }> };

const WORKFLOWS: { title: string; steps: WorkflowStep[] }[] = [
  {
    title: 'An employee clocks in',
    steps: [
      { label: 'Clock-in', Icon: Clock3 },
      { label: 'Payroll run', Icon: Banknote },
      { label: 'QuickBooks journal', Icon: Landmark },
    ],
  },
  {
    title: 'A client pays an invoice',
    steps: [
      { label: 'Invoice sent', Icon: FileText },
      { label: 'Stripe payment', Icon: CreditCard },
      { label: 'QuickBooks sync', Icon: BookOpenCheck },
    ],
  },
];

function ConnectedWorkflow() {
  return (
    <section className="py-20 lg:py-24 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="text-center max-w-2xl mx-auto mb-12 lg:mb-14"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Connected, not bundled</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            Modules that talk to each other.
          </motion.h2>
          <motion.p variants={FADE_UP} className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-xl mx-auto">
            One event updates every module that needs it — nothing to re-key, nothing to
            reconcile by hand at month-end.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5"
        >
          {WORKFLOWS.map((w) => (
            <motion.div
              key={w.title}
              variants={FADE_UP}
              className="hover-lift rounded-3xl p-7 lg:p-8 bg-white/85 dark:bg-white/[0.05] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
                {w.title}
              </p>
              <div className="mt-5 flex items-center gap-2 flex-wrap">
                {w.steps.map((s, i) => (
                  <React.Fragment key={s.label}>
                    {i > 0 && (
                      <ArrowRight
                        className="h-4 w-4 text-coral/60 shrink-0"
                        aria-hidden="true"
                      />
                    )}
                    <span className="inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-pearl-50 dark:bg-white/[0.06] ring-1 ring-ink/8 dark:ring-white/10 text-[13px] font-semibold text-ink dark:text-white">
                      <s.Icon className="h-4 w-4 text-coral" />
                      {s.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 5. TESTIMONIALS (live reviews, placeholder fallback)
 * ───────────────────────────────────────────────────────────── */

const PLACEHOLDER_TESTIMONIALS = [
  {
    tag: 'Studio · 8 people',
    body: 'Quote awaiting customer approval. The numbers (hours saved, payroll runs, invoices sent) will be filled in from real workspace data.',
  },
  {
    tag: 'Trades · 22 people',
    body: 'Quote awaiting customer approval. We\'ll publish with the customer name, role, and a measurable result — never before.',
  },
];

function Testimonials() {
  const [reviews, setReviews] = React.useState<Review[] | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listReviews({ limit: 2 })
      .then((rs) => {
        if (!cancelled) setReviews(rs);
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasLive = !loading && reviews && reviews.length > 0;

  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Customers</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            {hasLive
              ? <>Real teams, <em className="font-serif italic text-coral">on the record.</em></>
              : 'Real teams will tell the story — soon.'}
          </motion.h2>
          <motion.p
            variants={FADE_UP}
            className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl"
          >
            {hasLive
              ? 'Operators using Worqly today — names, companies, and quotes approved on the record.'
              : 'We\'re a young product and we won\'t run fake quotes. The first cohort of customer stories lands here as soon as they\'re approved.'}
          </motion.p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="rounded-3xl p-8 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card animate-pulse"
              >
                <span className="block h-5 w-32 rounded-full bg-ink/10 dark:bg-white/10" />
                <div className="mt-5 space-y-2">
                  <div className="h-4 w-full rounded-full bg-ink/10 dark:bg-white/10" />
                  <div className="h-4 w-11/12 rounded-full bg-ink/10 dark:bg-white/10" />
                  <div className="h-4 w-8/12 rounded-full bg-ink/10 dark:bg-white/10" />
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-ink/10 dark:bg-white/10" />
                  <span className="h-3 w-40 rounded-full bg-ink/10 dark:bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        ) : hasLive ? (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={STAGGER}
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            {reviews!.map((r) => (
              <motion.figure
                key={r.id}
                variants={FADE_UP}
                className="hover-lift rounded-3xl p-8 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-coral/10 text-coral-700 dark:bg-coral/15 dark:text-coral-400 text-[11px] font-bold uppercase tracking-[0.10em]">
                    {[r.sector, r.company].filter(Boolean).join(' · ') || 'Worqly customer'}
                  </span>
                  {r.rating ? <Stars rating={r.rating} /> : null}
                </div>
                <blockquote className="mt-5 text-[16px] leading-relaxed text-ink/80 dark:text-white/80">
                  "{r.body}"
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <Avatar name={r.name} url={r.avatar_url} size="sm" />
                  <span className="text-[13px] text-ink/65 dark:text-white/65">
                    <span className="font-semibold text-ink dark:text-white">{r.name}</span>
                    {r.role ? <span className="text-ink/55 dark:text-white/55">{` · ${r.role}`}</span> : null}
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={STAGGER}
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            {PLACEHOLDER_TESTIMONIALS.map((t) => (
              <motion.figure
                key={t.tag}
                variants={FADE_UP}
                className="hover-lift rounded-3xl p-8 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
              >
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-coral/10 text-coral-700 dark:bg-coral/15 dark:text-coral-400 text-[11px] font-bold uppercase tracking-[0.10em]">
                  {t.tag}
                </span>
                <blockquote className="mt-5 text-[16px] leading-relaxed text-ink/80 dark:text-white/80">
                  "{t.body}"
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span
                    aria-hidden
                    className="h-10 w-10 rounded-full bg-pearl-200 dark:bg-white/10 ring-1 ring-ink/8 dark:ring-white/10"
                  />
                  <span className="text-[13px] text-ink/55 dark:text-white/55">
                    Customer placeholder · publishing as soon as the quote is approved
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 6. PRICING TEASER
 * ───────────────────────────────────────────────────────────── */

type ModuleRow = {
  /** module_prices key for live overlay. */
  key: keyof typeof FALLBACK_PRICES;
  name: string;
  base: string;        // monthly base
  perEmployee: string; // per-employee rate ('—' for flat modules)
  note?: string;
};

// Base/per-employee below are FALLBACKS — live values come from
// GET /api/public/site/prices (module_prices, super-admin editable)
// via useTeaserPricing(). Names/notes always come from here.
const fmtUsd = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

const MODULE_ROWS: ModuleRow[] = [
  { key: 'core',       name: 'Core',              base: fmtUsd(FALLBACK_PRICES.core),       perEmployee: '—', note: 'Dashboard, roles, security, support + email/expenses/payments' },
  { key: 'invoicing',  name: 'Invoicing',          base: fmtUsd(FALLBACK_PRICES.invoicing),  perEmployee: '—', note: 'PDFs, Stripe links, reminders' },
  { key: 'projects',   name: 'Projects',           base: fmtUsd(FALLBACK_PRICES.projects),   perEmployee: '—', note: 'Tasks, time, margin' },
  { key: 'esign',      name: 'E-Sign',             base: fmtUsd(FALLBACK_PRICES.esign),      perEmployee: '—', note: 'Legally binding e-signature' },
  { key: 'hr',         name: 'Time & Attendance',  base: '$0', perEmployee: `${fmtUsd(FALLBACK_PRICES.hr)} / ${SEAT_LABEL.hr}`, note: 'Clock-in, attendance, leave' },
  { key: 'monitoring', name: 'Monitoring',         base: '$0', perEmployee: `${fmtUsd(FALLBACK_PRICES.monitoring)} / ${SEAT_LABEL.monitoring}`, note: 'Opt-in desktop agent' },
  {
    key: 'payroll', name: 'Payroll',
    base: fmtUsd(FALLBACK_SEAT_BASES.payroll),
    perEmployee: `${fmtUsd(FALLBACK_PRICES.payroll)} / ${SEAT_LABEL.payroll} beyond ${FALLBACK_INCLUDED_SEATS.payroll}`,
    note: `Runs, payslips, bank export. Includes ${FALLBACK_INCLUDED_SEATS.payroll} employees.`,
  },
];

/** Teaser rows + the "example workspace" total, overlaid with live prices. */
function useTeaserPricing(): { rows: ModuleRow[]; exampleTotal: string } {
  const live = useLivePrices();
  return React.useMemo(() => {
    const payrollIncluded = live?.['payroll']?.included_seats || FALLBACK_INCLUDED_SEATS.payroll;
    const rows = MODULE_ROWS.map((r) => {
      const lp = live?.[r.key];
      if (!lp) return r;
      if (r.key === 'payroll') {
        return {
          ...r,
          base: fmtUsd(lp.amount_per_month),
          perEmployee: `${fmtUsd(lp.seat_amount_per_month)} / ${SEAT_LABEL.payroll} beyond ${payrollIncluded}`,
          note: `Runs, payslips, bank export. Includes ${payrollIncluded} employees.`,
        };
      }
      const isMetered = r.key === 'hr' || r.key === 'monitoring';
      return {
        ...r,
        base: isMetered ? '$0' : fmtUsd(lp.amount_per_month),
        perEmployee: isMetered
          ? `${fmtUsd(lp.seat_amount_per_month)} / ${SEAT_LABEL[r.key as 'hr' | 'monitoring']}`
          : '—',
      };
    });

    // Example workspace: 5 employees, 2 monitored, Invoicing + Time &
    // Attendance + Monitoring + Payroll on. Payroll is under its included
    // allowance at 5 employees, so it's just its $15 base fee:
    // 9 + 19 + (3×2) + (2×7) + 15 = $63.
    // Computed with the same helper the pricing calculator uses.
    const corePrice = live?.['core']?.amount_per_month ?? FALLBACK_PRICES.core;
    const invoicingPrice = live?.['invoicing']?.amount_per_month ?? FALLBACK_PRICES.invoicing;
    const hrSeatPrice = live?.['hr']?.seat_amount_per_month ?? FALLBACK_PRICES.hr;
    const monitoringSeatPrice = live?.['monitoring']?.seat_amount_per_month ?? FALLBACK_PRICES.monitoring;
    const payrollBasePrice = live?.['payroll']?.amount_per_month ?? FALLBACK_SEAT_BASES.payroll;
    const payrollSeatPrice = live?.['payroll']?.seat_amount_per_month ?? FALLBACK_PRICES.payroll;
    const { total } = computeMonthlyTotal({
      corePrice, invoicingPrice,
      projectsPrice: 0, esignPrice: 0,
      hrSeatPrice, monitoringSeatPrice,
      payrollBasePrice, payrollSeatPrice, payrollIncluded,
      invoicingOn: true, projectsOn: false, esignOn: false,
      hrOn: true, monitoringOn: true, payrollOn: true,
      employees: 5, monitored: 2,
    });
    return { rows, exampleTotal: fmtUsd(total) };
  }, [live]);
}

function PricingTeaser() {
  const { rows, exampleTotal } = useTeaserPricing();
  return (
    <section id="pricing-teaser" className="py-20 lg:py-24 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left: stat + copy */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={STAGGER}
            className="lg:col-span-5"
          >
            <motion.div variants={FADE_UP}>
              <Eyebrow className="!text-coral">Pricing</Eyebrow>
            </motion.div>
            <motion.h2
              variants={FADE_UP}
              className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
            >
              Pay only for what you use.
            </motion.h2>
            <motion.p
              variants={FADE_UP}
              className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-md"
            >
              Core is a flat $9/mo. Some modules add a flat fee; Time & Attendance and Monitoring
              bill per employee — and an employee is billed clock-in or monitored, never both.
              Payroll is $15/mo including 10 employees, then $1.50 each beyond that. No bundles,
              no annual lock-in.
            </motion.p>

            <motion.div variants={FADE_UP} className="mt-8 rounded-3xl bg-ink-900 text-white p-6 ring-1 ring-white/10 shadow-card-lg">
              <Eyebrow className="!text-white/55">Example workspace</Eyebrow>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-stat-xl font-bold tabular-nums leading-none">{exampleTotal}</span>
                <span className="text-[14px] text-white/65">/ month</span>
              </div>
              <p className="mt-3 text-[13px] text-white/65">
                5 employees, 2 monitored · Invoicing + Time & Attendance + Monitoring + Payroll · 7
                days free first
              </p>
            </motion.div>

            <motion.div variants={FADE_UP} className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
              >
                See full pricing
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-white text-ink text-[13.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-100 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
              >
                Start free
              </Link>
            </motion.div>
          </motion.div>

          {/* Right: module table */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={FADE_UP}
            className="lg:col-span-7"
          >
            <div className="rounded-3xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 px-6 py-4 border-b border-ink/8 dark:border-white/10">
                <div className="col-span-5 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
                  Module
                </div>
                <div className="col-span-3 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55 text-right">
                  Base / mo
                </div>
                <div className="col-span-4 text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55 text-right">
                  Per employee / mo
                </div>
              </div>

              {/* Rows */}
              <ul>
                {rows.map((m, i) => (
                  <li
                    key={m.name}
                    className={
                      'grid grid-cols-12 gap-2 px-6 py-4 items-center ' +
                      (i < rows.length - 1
                        ? 'border-b border-ink/5 dark:border-white/5'
                        : '')
                    }
                  >
                    <div className="col-span-5 min-w-0">
                      <div className="text-[14px] font-semibold text-ink dark:text-white truncate">
                        {m.name}
                      </div>
                      {m.note && (
                        <div className="mt-0.5 text-[12px] text-ink/55 dark:text-white/55 truncate">
                          {m.note}
                        </div>
                      )}
                    </div>
                    <div className="col-span-3 text-right">
                      <span className="text-[14px] font-bold tabular-nums text-ink dark:text-white">
                        {m.base}
                      </span>
                    </div>
                    <div className="col-span-4 text-right">
                      <span className="text-[13px] font-bold tabular-nums text-coral">
                        {m.perEmployee === '—'
                          ? <span className="text-ink/40 dark:text-white/40">—</span>
                          : m.perEmployee}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="px-6 py-4 bg-pearl-50/80 dark:bg-white/[0.02] text-[12.5px] text-ink/55 dark:text-white/55">
                Free for 7 days · No card required at signup · Card converts the trial to paid
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 7. FAQ
 * ───────────────────────────────────────────────────────────── */

function FAQ() {
  const items = [
    {
      q: 'Is there really no card to start?',
      a: 'Yes. Sign up with an email, pick a workspace name, and you\'re in. We don\'t ask for a card until you\'re ready to convert the trial to paid — usually before day 8.',
    },
    {
      q: 'How does per-employee pricing work?',
      a: 'Core is a flat $9/mo. Time & Attendance ($2) and Monitoring ($7) bill per employee — clock-in and monitored respectively; an employee is billed one or the other, never both. Payroll is different: it\'s a flat $15/mo that includes your first 10 active employees, then $1.50/mo for each additional one, and it always stacks on top.',
    },
    {
      q: 'Can I add modules later? When am I actually charged?',
      a: 'Anytime — modules turn on with one click from your workspace settings. Billing is in advance: on your renewal date we charge your card for the month ahead, based on your workspace as it stands that day. Adding or removing a flat-priced module (Invoicing, Projects, E-Sign) mid-month is prorated for the remaining days — the per-employee modules aren\'t prorated either way. Employee counts (for Time & Attendance, Monitoring, and Payroll) are taken on the charge date and aren\'t prorated either way — hire someone mid-month and they\'re included from your next charge at no extra cost now, and deactivate someone mid-month and they still count toward the charge already issued, with no partial refund.',
    },
    {
      q: 'What happens if I don\'t add a card?',
      a: 'After the 7-day trial ends, your modules pause. Your data stays put — nothing is deleted just because the trial lapsed. Add a card any time during or after the trial to resume where you left off. Data is only purged if you ask us to delete the workspace, and even then after a 30-day grace window.',
    },
    {
      q: 'How is my data secured?',
      a: 'Worqly is self-hosted on dedicated servers we run and administer ourselves. Data is encrypted in transit (TLS) and at rest, every workspace is isolated by Postgres row-level security enforced at the database, and every action is written to an audit log. Card details are handled by Stripe and never touch our servers. Full detail — including what we have not built yet — is on the security page.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Yes — no annual lock-in, no exit fee. Cancel from the billing page and we pro-rate the current month. Invoices, clients, employees, payroll and expenses all export as CSV, so take your data with you before you delete the workspace.',
    },
  ];

  const [open, setOpen] = React.useState<number | null>(0);
  const faqSchema = React.useMemo(() => buildFaqPageSchema(items), [items]);

  return (
    <section id="faq" className="py-20 lg:py-24">
      <JsonLd id="faq" schema={faqSchema} />
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="text-center mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">FAQ</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            Six honest answers before you sign up.
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="space-y-3"
        >
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={it.q}
                variants={FADE_UP}
                className="rounded-2xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left focus-coral rounded-2xl"
                >
                  <span className="text-[15px] font-semibold text-ink dark:text-white">{it.q}</span>
                  <ChevronDown
                    className={
                      'h-4 w-4 shrink-0 transition-transform text-ink/55 dark:text-white/55 ' +
                      (isOpen ? 'rotate-180' : '')
                    }
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-[14px] text-ink/70 dark:text-white/70 leading-relaxed">{it.a}</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 8. FOOTER CTA — email capture → /signup
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    // Forward to /signup with the email pre-filled. /signup owns the
    // actual API call and CAPTCHA flow — we never POST from here.
    const params = new URLSearchParams();
    if (email.trim()) params.set('email', email.trim());
    const qs = params.toString();
    window.location.href = qs ? `/signup?${qs}` : '/signup';
  }

  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
                Start free
              </p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Start free in 60 seconds.
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Seven days free, no card to start. Pick the modules you actually need — pay only for the
                people who use them.
              </p>
            </div>

            <div className="lg:col-span-5">
              <form
                onSubmit={onSubmit}
                className="rounded-2xl bg-white/15 backdrop-blur-md ring-1 ring-white/25 p-2 flex flex-col sm:flex-row sm:items-center gap-2"
              >
                <label htmlFor="cta-email" className="sr-only">
                  Work email
                </label>
                <input
                  id="cta-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 min-w-0 w-full h-11 px-4 rounded-full bg-white text-ink text-[14px] placeholder:text-ink/40 outline-none focus:ring-2 focus:ring-white/60"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex shrink-0 w-full sm:w-auto items-center justify-center gap-1.5 h-11 px-5 rounded-full bg-ink-900 text-white text-[13.5px] font-semibold hover:bg-black transition-colors disabled:opacity-60"
                >
                  Start free trial
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 justify-center sm:justify-start">
                <p className="text-[12px] text-white/75">
                  No card required. Add billing before day 8 to keep going.
                </p>
                <Link
                  to="/contact?topic=demo"
                  className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-white underline underline-offset-2 hover:text-white/85 transition-colors"
                >
                  Or book a demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
